import {
    legacyApi,
    InquiryDetail,
    ProjectType,
    EstimateDetail,
    DocumentAttachment,
    WorkflowEntityDefinition,
    WorkflowEventDefinition,
    WorkflowHistoryEntry,
} from '../../../services/legacyApi';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Trash2, ExternalLink, FileText, Info, Plus, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { useConfirm } from '../../../hooks/use-confirm';
import { TransitionActionBar } from '../components/TransitionActionBar';
import { StatusGuidePanel } from '../components/StatusGuidePanel';
import { StatusHistoryPanel } from '../components/StatusHistoryPanel';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { useAuth } from '../../../context/AuthContext';
import { DocumentAttachmentsCard } from '../components/DocumentAttachmentsCard';
import { DocumentPreviewDialog } from '../components/DocumentPreviewDialog';

export const LegacyInquiryDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const { hasPermission } = useAuth();
    const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
    const [estimate, setEstimate] = useState<EstimateDetail | null>(null);
    const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
    const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [previewDocument, setPreviewDocument] = useState<DocumentAttachment | null>(null);
    const [workflowDefinition, setWorkflowDefinition] = useState<WorkflowEntityDefinition | null>(null);
    const [nextActions, setNextActions] = useState<WorkflowEventDefinition[]>([]);
    const [history, setHistory] = useState<WorkflowHistoryEntry[]>([]);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [transitionBusy, setTransitionBusy] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'inquiry' | 'estimate'>('inquiry');
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (!id) return;
        loadInquiry();
        legacyApi.getProjectTypes().then(setProjectTypes).catch(console.error);
    }, [id]);

    useEffect(() => {
        if (activeTab === 'estimate' && inquiry?.linked_estimate_id && !estimate) {
            loadEstimate();
        }
    }, [activeTab, inquiry]);

    const loadWorkflowMeta = async (inquiryId: number) => {
        try {
            const [definition, actions, historyResult] = await Promise.all([
                legacyApi.workflow.getDefinition('INQUIRY'),
                legacyApi.workflow.getNextActions('INQUIRY', inquiryId),
                legacyApi.workflow.getHistory('INQUIRY', inquiryId),
            ]);
            setWorkflowDefinition(definition);
            setNextActions(actions.next_actions || []);
            setHistory(historyResult.history || []);
        } catch (error) {
            console.error('Failed to load inquiry workflow metadata', error);
        }
    };

    const loadInquiryDocuments = async (inquiryId: number, options?: { silent?: boolean }) => {
        if (!options?.silent) setDocumentsLoading(true);
        try {
            const rows = await legacyApi.inquiries.listDocuments(inquiryId, 'ENQUIRY_CONTENT');
            setDocuments(Array.isArray(rows) ? rows : []);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load enquiry documents');
            setDocuments([]);
        } finally {
            if (!options?.silent) setDocumentsLoading(false);
        }
    };

    const loadInquiry = async () => {
        setLoading(true);
        try {
            const inquiryId = parseInt(id!);
            const data = await legacyApi.inquiries.getDetail(inquiryId);
            setInquiry(data);
            await Promise.all([
                loadWorkflowMeta(inquiryId),
                loadInquiryDocuments(inquiryId, { silent: true }),
            ]);
        } catch (e: any) {
            console.error(e);
            toast.error(e.message || 'Failed to load inquiry');
        } finally {
            setLoading(false);
        }
    };

    const loadEstimate = async () => {
        if (!inquiry?.linked_estimate_id) return;
        try {
            const data = await legacyApi.estimates.getDetail(inquiry.linked_estimate_id);
            setEstimate(data);
        } catch (e) {
            console.error(e);
        }
    };

    const handleSave = async () => {
        if (!inquiry) return;
        setBusy(true);
        try {
            await legacyApi.inquiries.update(inquiry.inquiry_id, {
                project_scope: inquiry.project_scope,
                project_description: inquiry.project_description,
                attention: inquiry.attention,
                enquiry_ref: inquiry.enquiry_ref,
                customer_address: inquiry.customer_address,
                project_type_id: inquiry.project_type_id,
                followup_date: inquiry.followup_date,
                remarks: inquiry.remarks,
                segment: inquiry.segment,
            });
            toast.success('Inquiry updated successfully!');
            setEditMode(false);
            await loadInquiry();
        } catch (e: any) {
            console.error(e);
            toast.error(e.message || 'Failed to update inquiry');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!inquiry) return;

        const confirmed = await confirm('Are you sure you want to delete this Inquiry?', {
            description: 'This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'destructive',
        });

        if (!confirmed) return;

        setBusy(true);
        try {
            await legacyApi.inquiries.delete(inquiry.inquiry_id);
            toast.success('Inquiry deleted.');
            setEditMode(false);
            navigate('/legacy/inquiries');
        } catch (e) {
            console.error(e);
            toast.error('Failed to delete inquiry');
        } finally {
            setBusy(false);
        }
    };

    const handleTransition = async (actionCode: string) => {
        if (!inquiry) return;

        let shouldContinue = true;
        if (actionCode === 'convert') {
            shouldContinue = await confirm('Convert this Inquiry to a new Estimate?', {
                confirmText: 'Convert',
            });
        }
        if (!shouldContinue) return;

        setTransitionBusy(actionCode);
        try {
            if (actionCode === 'convert') {
                const result = await legacyApi.inquiries.convert(inquiry.inquiry_id, 'Converted from workflow action bar');
                toast.success('Inquiry converted to estimate');
                if (result?.estimate_id) {
                    navigate(`/legacy/estimates/${result.estimate_id}`);
                    return;
                }
            } else {
                await legacyApi.inquiries.transition(inquiry.inquiry_id, actionCode, 'Updated from inquiry detail page');
                toast.success('Inquiry status updated');
            }
            await loadInquiry();
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to update status');
        } finally {
            setTransitionBusy(null);
        }
    };

    const handleUploadInquiryDocument = async (payload: { file: File; note?: string; markCurrent: boolean }) => {
        if (!inquiry) return;
        try {
            await legacyApi.inquiries.uploadDocument(inquiry.inquiry_id, {
                file: payload.file,
                category: 'ENQUIRY_CONTENT',
                note: payload.note,
                mark_current: payload.markCurrent,
            });
            toast.success('Enquiry content uploaded');
            await loadInquiryDocuments(inquiry.inquiry_id, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to upload enquiry content');
            throw error;
        }
    };

    const handleSetCurrentInquiryDocument = async (docId: number) => {
        if (!inquiry) return;
        try {
            await legacyApi.inquiries.setDocumentCurrent(inquiry.inquiry_id, docId);
            toast.success('Current enquiry content updated');
            await loadInquiryDocuments(inquiry.inquiry_id, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update current document');
            throw error;
        }
    };

    const handleDownloadInquiryDocument = async (docId: number) => {
        if (!inquiry) return;
        try {
            await legacyApi.inquiries.downloadDocument(inquiry.inquiry_id, docId);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to download document');
            throw error;
        }
    };

    const handlePreviewInquiryDocument = (doc: DocumentAttachment) => {
        setPreviewDocument(doc);
    };

    const handleDeleteInquiryDocument = async (docId: number) => {
        if (!inquiry) return;
        const allowed = await confirm('Delete this enquiry document?', {
            description: 'The document will be archived (soft deleted).',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!allowed) return;
        try {
            await legacyApi.inquiries.deleteDocument(inquiry.inquiry_id, docId);
            toast.success('Document deleted');
            await loadInquiryDocuments(inquiry.inquiry_id, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to delete enquiry document');
            throw error;
        }
    };

    const formatInquiryId = (inquiryId: number) => `HC/ENQ/${String(inquiryId).padStart(4, '0')}`;

    if (loading) {
        return (
            <div className="flex h-[400px] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!inquiry) return <div className="p-8 text-center text-muted-foreground">Inquiry not found</div>;

    const statusDefinition = workflowDefinition?.statuses?.find((status) => status.status_id === inquiry.inquiry_status_id);
    const canViewInquiryDocuments = hasPermission('sales.inquiry.document.view');
    const canUploadInquiryDocuments = hasPermission('sales.inquiry.document.upload');
    const canDownloadInquiryDocuments = hasPermission('sales.inquiry.document.download');
    const canDeleteInquiryDocuments = hasPermission('sales.inquiry.document.delete');

    return (
        <div className="space-y-6 w-full pb-12">
            <DocumentPreviewDialog
                open={Boolean(previewDocument)}
                document={previewDocument}
                previewUrl={previewDocument && inquiry ? legacyApi.inquiries.previewDocumentUrl(inquiry.inquiry_id, previewDocument.doc_id) : null}
                onOpenChange={(open) => !open && setPreviewDocument(null)}
                onDownload={(doc) => handleDownloadInquiryDocument(doc.doc_id)}
            />
            <div className="flex justify-between items-end">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/legacy/inquiries')} className="-ml-2">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">
                            {`Inquiry ${formatInquiryId(inquiry.inquiry_id)}`}
                        </h1>
                        <p className="text-muted-foreground">{inquiry.customer_name}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {!editMode && (
                        <Button variant="outline" onClick={() => setEditMode(true)} disabled={busy}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                        </Button>
                    )}
                    {editMode && (
                        <Button onClick={handleSave} disabled={busy}>
                            <Save className="w-4 h-4 mr-2" /> Save Changes
                        </Button>
                    )}
                    <Button variant="destructive" size="icon" onClick={handleDelete} disabled={busy} title="Delete Inquiry">
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <div className="border-b">
                <nav className="flex gap-4">
                    {[
                        { id: 'inquiry', label: 'Inquiry Details', icon: Info },
                        { id: 'estimate', label: 'Linked Estimate', icon: FileText },
                    ].map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as 'inquiry' | 'estimate')}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                {activeTab === 'inquiry' ? (
                    <div className="space-y-6">
                        <div className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Project Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Project Type</label>
                                            {editMode ? (
                                                <select
                                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                                    value={inquiry.project_type_id}
                                                    onChange={(e) => setInquiry({ ...inquiry, project_type_id: parseInt(e.target.value, 10) })}
                                                >
                                                    {projectTypes.map((projectType) => (
                                                        <option key={projectType.id} value={projectType.id}>{projectType.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className="h-9 flex items-center px-2 bg-slate-50 rounded border border-transparent text-sm">
                                                    {projectTypes.find((projectType) => projectType.id === inquiry.project_type_id)?.name || '-'}
                                                </div>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Segment</label>
                                            {editMode ? (
                                                <select
                                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                                    value={inquiry.segment || ''}
                                                    onChange={(e) => setInquiry({ ...inquiry, segment: e.target.value })}
                                                >
                                                    <option value="">Select Segment...</option>
                                                    <option value="Marine">Marine</option>
                                                    <option value="Planter">Planter</option>
                                                    <option value="Projects">Projects</option>
                                                </select>
                                            ) : (
                                                <div className="h-9 flex items-center px-2 bg-slate-50 rounded border border-transparent text-sm">{inquiry.segment || '-'}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium">Project Scope</label>
                                        {editMode ? (
                                            <Input
                                                value={inquiry.project_scope || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInquiry({ ...inquiry, project_scope: e.target.value })}
                                            />
                                        ) : (
                                            <div className="h-9 flex items-center px-2 bg-slate-50 rounded border border-transparent text-sm">{inquiry.project_scope || '-'}</div>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-medium">Project Description</label>
                                        {editMode ? (
                                            <textarea
                                                className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                                                value={inquiry.project_description || ''}
                                                onChange={(e) => setInquiry({ ...inquiry, project_description: e.target.value })}
                                            />
                                        ) : (
                                            <div className="min-h-[2.5rem] px-2 py-1 bg-slate-50 rounded border border-transparent text-sm whitespace-pre-line">{inquiry.project_description || '-'}</div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Attention</label>
                                            {editMode ? (
                                                <Input
                                                    value={inquiry.attention || ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInquiry({ ...inquiry, attention: e.target.value })}
                                                    placeholder="POC name"
                                                />
                                            ) : (
                                                <div className="h-9 flex items-center px-2 bg-slate-50 rounded border border-transparent text-sm">{inquiry.attention || '-'}</div>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Enquiry Ref</label>
                                            {editMode ? (
                                                <Input
                                                    value={inquiry.enquiry_ref || ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInquiry({ ...inquiry, enquiry_ref: e.target.value })}
                                                    placeholder="Mail Ref/Whatsapp Ref"
                                                />
                                            ) : (
                                                <div className="h-9 flex items-center px-2 bg-slate-50 rounded border border-transparent text-sm">{inquiry.enquiry_ref || '-'}</div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Customer Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs text-muted-foreground font-medium uppercase">Customer Name</label>
                                            <div className="font-semibold">{inquiry.customer_name}</div>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs text-muted-foreground font-medium uppercase">Contact Number</label>
                                            <div className="font-medium text-blue-600">{inquiry.customer_tel || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="space-y-1 border-t pt-2">
                                        <label className="text-xs text-muted-foreground font-medium uppercase">Address</label>
                                        {editMode ? (
                                            <textarea
                                                className="flex min-h-[90px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                                                value={inquiry.customer_address || ''}
                                                onChange={(e) => setInquiry({ ...inquiry, customer_address: e.target.value })}
                                            />
                                        ) : (
                                            <div className="text-sm whitespace-pre-line">{inquiry.customer_address || '-'}</div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Internal Remarks</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {editMode ? (
                                        <textarea
                                            className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                                            value={inquiry.remarks || ''}
                                            onChange={(e) => setInquiry({ ...inquiry, remarks: e.target.value })}
                                        />
                                    ) : (
                                        <div className="min-h-[2.5rem] px-2 py-1 bg-slate-50 rounded border border-transparent text-sm whitespace-pre-line">{inquiry.remarks || '-'}</div>
                                    )}
                                </CardContent>
                            </Card>

                            {canViewInquiryDocuments && (
                                <DocumentAttachmentsCard
                                    title="Enquiry Contents"
                                    emptyLabel="No enquiry content files uploaded."
                                    documents={documents}
                                    loading={documentsLoading}
                                    canUpload={canUploadInquiryDocuments}
                                    canDownload={canDownloadInquiryDocuments}
                                    canDelete={canDeleteInquiryDocuments}
                                    onUpload={handleUploadInquiryDocument}
                                    onSetCurrent={handleSetCurrentInquiryDocument}
                                    onPreview={handlePreviewInquiryDocument}
                                    onDownload={handleDownloadInquiryDocument}
                                    onDelete={handleDeleteInquiryDocument}
                                    onRefresh={() => loadInquiryDocuments(inquiry.inquiry_id)}
                                />
                            )}
                        </div>

                        <WorkflowRightSidebar mode="overlay" title="Inquiry Workflow & Status">
                            <WorkflowRightPanel
                                title="Workflow & Status"
                                description="Current status, next action, and prechecks"
                            >
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-muted-foreground">Enquiry Date</label>
                                    <div className="font-medium">
                                        {inquiry.date_enquired ? new Date(inquiry.date_enquired).toLocaleDateString(undefined, { dateStyle: 'long' }) : '-'}
                                    </div>
                                </div>

                                <TransitionActionBar
                                    actions={nextActions}
                                    onAction={handleTransition}
                                    disabled={busy || editMode || transitionBusy !== null}
                                    busyEventCode={transitionBusy}
                                    precheckEntity="INQUIRY"
                                    precheckEntityId={inquiry.inquiry_id}
                                />

                                <StatusGuidePanel
                                    statusLabel={statusDefinition?.status_label || inquiry.status_name || `Status #${inquiry.inquiry_status_id}`}
                                    statusMeaning={statusDefinition?.status_meaning || ''}
                                    nextActions={nextActions}
                                />
                                <FlowCoachPanel entity="INQUIRY" entityId={inquiry.inquiry_id} />

                                <div className="space-y-1 pt-2">
                                    <label className="text-sm font-medium">Follow-up Date</label>
                                    {editMode ? (
                                        <Input
                                            type="date"
                                            value={inquiry.followup_date ? inquiry.followup_date.split('T')[0] : ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInquiry({ ...inquiry, followup_date: e.target.value })}
                                        />
                                    ) : (
                                        <div className="h-9 flex items-center px-2 bg-slate-50 rounded border border-transparent text-sm">
                                            {inquiry.followup_date ? inquiry.followup_date.split('T')[0] : '-'}
                                        </div>
                                    )}
                                </div>
                            </WorkflowRightPanel>

                            <WorkflowRightPanel
                                title="Status History"
                                description="Audit log of status changes"
                                defaultOpen={false}
                            >
                                <StatusHistoryPanel history={history} />
                            </WorkflowRightPanel>

                            {inquiry.linked_estimate_id && (
                                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                                    <div className="flex items-center gap-2 text-primary font-semibold">
                                        <FileText className="w-4 h-4" />
                                        <span>Linked Estimate</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">This inquiry is already converted to an estimate.</p>
                                    <Button variant="secondary" size="sm" className="w-full" onClick={() => setActiveTab('estimate')}>
                                        View Estimate Details
                                    </Button>
                                </div>
                            )}
                        </WorkflowRightSidebar>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {!inquiry.linked_estimate_id ? (
                            <Card className="p-12 text-center bg-muted/20 border-dashed">
                                <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold mb-2">No Linked Estimate</h3>
                                <p className="text-muted-foreground max-w-sm mx-auto mb-6">Create an estimate from this inquiry to see project costs and parts breakdown.</p>
                                <Button
                                    onClick={() => handleTransition('convert')}
                                    disabled={busy || transitionBusy !== null}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Generate Estimate Now
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/legacy/estimates/quick-quote?inquiryId=${inquiry.inquiry_id}`)}
                                    disabled={busy || transitionBusy !== null}
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Quick Quotation
                                </Button>
                            </Card>
                        ) : (
                            <div className="space-y-6">
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0">
                                        <CardTitle className="text-lg">Estimate Summary</CardTitle>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate(`/legacy/estimates/${inquiry.linked_estimate_id}`)}
                                        >
                                            <ExternalLink className="w-4 h-4 mr-2" /> Full View
                                        </Button>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                            <div className="space-y-1">
                                                <label className="text-xs text-muted-foreground font-semibold uppercase">Estimate Number</label>
                                                <div className="text-lg font-bold text-primary">HC/EST/{String(inquiry.linked_estimate_id).padStart(4, '0')}</div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-xs text-muted-foreground font-semibold uppercase">Created Date</label>
                                                <div className="font-medium font-mono">{estimate?.date_created ? new Date(estimate.date_created).toLocaleDateString() : '-'}</div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-xs text-muted-foreground font-semibold uppercase">Project Type</label>
                                                <div className="font-medium">{projectTypes.find((projectType) => projectType.id === inquiry.project_type_id)?.name || '-'}</div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-xs text-muted-foreground font-semibold uppercase">Total Value</label>
                                                <div className="text-lg font-bold text-green-600">
                                                    AED {(estimate?.total_price || estimate?.parts.reduce((sum, part) => sum + (part.selling_price_total || 0), 0) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-lg">Parts Breakdown</CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        <div className="overflow-x-auto">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="bg-muted/50 text-xs text-muted-foreground font-semibold border-y uppercase tracking-wider">
                                                        <th className="px-4 py-3 text-left w-28">Part #</th>
                                                        <th className="px-4 py-3 text-left">Part Name</th>
                                                        <th className="px-4 py-3 text-left">Dimension</th>
                                                        <th className="px-4 py-3 text-center w-20">Qty</th>
                                                        <th className="px-4 py-3 text-right w-28">Area (m2)</th>
                                                        <th className="px-4 py-3 text-right w-28">Weight (kg)</th>
                                                        <th className="px-4 py-3 text-right w-36">Total Price</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y text-sm">
                                                    {estimate?.parts.map((part, idx) => (
                                                        <tr key={idx} className="hover:bg-muted/30 transition-colors">
                                                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                                                                HC/{inquiry.linked_estimate_id}/{String(part.estimate_part_id).padStart(4, '0')}
                                                            </td>
                                                            <td className="px-4 py-3 font-medium">{part.part_name}</td>
                                                            <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{part.dimension || '-'}</td>
                                                            <td className="px-4 py-3 text-center tabular-nums">{part.quantity}</td>
                                                            <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{part.area_m2.toFixed(2)}</td>
                                                            <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{part.weight_kgs.toFixed(2)}</td>
                                                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                                                                {(part.selling_price_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {(!estimate || estimate.parts.length === 0) && (
                                                        <tr>
                                                            <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground italic">No parts found for this estimate.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                                {estimate && estimate.parts.length > 0 && (
                                                    <tfoot>
                                                        <tr className="bg-muted/30 font-bold border-t">
                                                            <td colSpan={4} className="px-4 py-3 text-right">TOTAL</td>
                                                            <td className="px-4 py-3 text-right tabular-nums">
                                                                {estimate.parts.reduce((sum, part) => sum + part.area_m2, 0).toFixed(2)} m2
                                                            </td>
                                                            <td className="px-4 py-3 text-right tabular-nums">
                                                                {estimate.parts.reduce((sum, part) => sum + part.weight_kgs, 0).toFixed(2)} kg
                                                            </td>
                                                            <td className="px-4 py-3 text-right tabular-nums text-primary">
                                                                AED {(estimate.total_price || estimate.parts.reduce((sum, part) => sum + (part.selling_price_total || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
